import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Table } from '../components';
import TableTitle from '../components/TableTitle';
import Pagination from '../components/Pagination/Pagination';
import usePagination from '../components/Pagination/usePagination';
import { Checkbox } from '../components/Checkbox';
import FirmwareVersions from '../features/firmwarePolicies/FirmwareVersions';
import ControlPanel from '../features/firmwarePolicies/ControlPanel';
import { useFirmwarePoliciesStore } from '../features/firmwarePolicies/state/use-firmware-policies-store';
import useRole from '../hooks/use-role';
import { TEST_DEVICE_HINT } from '../features/firmwareTest/constants';

const LIMIT = 20;

const FirmwareUpdatePolicies = () => {
  const { data, total, firmwareVersions, isLoading, error, togglingId, fetchPolicies, togglePolicy, toggleTestDevice } = useFirmwarePoliciesStore();
  const { isMeridianOrg } = useRole();
  const { page, limit, updatePageUrl } = usePagination({ limit: LIMIT });
  const totalPages = Math.ceil(total / limit);

  // sort:
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // search:
  const [serialNumber, setSerialNumber] = useState('');

  const handleSort = useCallback(
    (newSortBy, newSortOrder) => {
      setSortBy(newSortBy || '');
      setSortOrder(newSortOrder || 'asc');
      updatePageUrl(1);
    },
    [updatePageUrl]
  );

  const handleSearch = useCallback(
    (value) => {
      setSerialNumber(value || '');
      if (page !== 1) updatePageUrl(1);
    },
    [page, updatePageUrl]
  );

  const handleToggle = useCallback(
    (row, field) => {
      if (togglingId === row.device_id) return;
      togglePolicy({ id: row.device_id, field, value: !row[field] });
    },
    [togglePolicy, togglingId]
  );

  const handleToggleTestDevice = useCallback(
    (row) => {
      if (togglingId === row.device_id) return;
      toggleTestDevice({ id: row.device_id, value: !row.is_test_device });
    },
    [toggleTestDevice, togglingId]
  );

  useEffect(() => {
    fetchPolicies({ page, count: limit, sortBy, sortOrder, serialNumber });
  }, [page, limit, sortBy, sortOrder, serialNumber]);

  const columns = useMemo(
    () => [
      {
        key: 'device_id',
        title: 'ID устройства',
        sortable: true,
        render: (value) => <div className="min-w-max">{value ?? '-'}</div>,
      },
      {
        key: 'serial_number',
        title: 'Серийный номер',
        sortable: true,
      },
      {
        key: 'user_id',
        title: 'ID пользователя',
        sortable: true,
      },
      {
        key: 'current_version',
        title: 'Текущая версия',
        sortable: true,
        render: (value) => <div>{value || '-'}</div>,
      },
      ...(isMeridianOrg
        ? [
            {
              key: 'actions',
              sortKey: 'is_test_device',
              sortable: true,
              title: 'Тестовый',
              hint: TEST_DEVICE_HINT,
              render: (row) => (
                <div className="flex items-center justify-center">
                  <Checkbox checked={Boolean(row?.is_test_device)} disabled={togglingId === row?.device_id} onChange={() => handleToggleTestDevice(row)} />
                </div>
              ),
            },
          ]
        : []),
      {
        key: 'actions',
        sortKey: 'is_update_allowed',
        sortable: true,
        title: <div className="text-center">Обновления разрешены</div>,
        render: (row) => (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={Boolean(row?.is_update_allowed)}
              disabled={togglingId === row?.device_id}
              onChange={() => handleToggle(row, 'is_update_allowed')}
            />
          </div>
        ),
      },
      {
        key: 'actions',
        sortKey: 'is_global_ban',
        sortable: true,
        title: <div className="text-center">Глобальная блокировка</div>,
        render: (row) => (
          <div className="flex items-center justify-center">
            <Checkbox checked={Boolean(row?.is_global_ban)} disabled={togglingId === row?.device_id} onChange={() => handleToggle(row, 'is_global_ban')} />
          </div>
        ),
      },
      {
        key: 'is_next_update_allowed',
        sortKey: 'is_next_update_allowed',
        sortable: true,
        title: <div className="text-center">Разрешение пользователя</div>,
        render: (row) => <div className="flex items-center justify-center">{row ? 'Разрешено' : 'Запрещено'}</div>,
      },
    ],
    [togglingId, handleToggle, handleToggleTestDevice, isMeridianOrg]
  );

  return (
    <Container>
      <div className="max-w-7xl mx-auto">
        <TableTitle title={'Обновления прошивок устройств'} />
      </div>

      <FirmwareVersions versions={firmwareVersions} loading={isLoading} onSearch={handleSearch} searchDefaultValue={serialNumber} />
      <ControlPanel />

      <Table columns={columns} data={data} loading={isLoading} error={error} sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} rowKey="device_id">
        {isLoading || error || totalPages === 0 ? null : <Pagination totalPages={totalPages} withLimit={true} initialLimit={LIMIT} />}
      </Table>
    </Container>
  );
};

export default FirmwareUpdatePolicies;
